import * as React from 'react';

import {useDeviceStore, selectIsMobile} from "features/ui/store/useDeviceStore";
import {getFormattedMessage} from "features/services/utils/language-helper";
import {renderPriceSpan} from "@organisms/ResidenceSearch/ResidenceFilters/helpers";
import {isAreaFilterActive, isPriceFilterActive} from "features/search/filters/helpers";
import {FILTERS_MIN_ROOMS, FILTERS_MAX_ROOMS} from "features/search/filters/constants";
import {
    useResidenceExplorerStore,
    selectFilters,
} from "features/residenceExplorer/store/useResidenceExplorerStore";

import {FilterPopout} from "@organisms/ResidenceSearch/ResidenceFilters/components/_filterPopout";

import {FormRangeSlider} from "components/forms/formComponents/FormRangeSlider";
import {
    FILTERS_MAX_AREA,
    FILTERS_MAX_PRICE,
    FILTERS_MIN_AREA,
    FILTERS_MIN_PRICE
} from "features/search/filters/constants";
import {RoomSelectorV2} from "@organisms/ResidenceSearch/ResidenceFilters/filters/_roomSelector";


export const ResidenceExplorerFilterButtons: React.FunctionComponent = ({}) => {
    const filters = useResidenceExplorerStore(selectFilters);
    const setPriceFilter = useResidenceExplorerStore(state => state.setPriceFilter);
    const setRoomsFilter = useResidenceExplorerStore(state => state.setRoomsFilter);
    const setAreaFilter = useResidenceExplorerStore(state => state.setAreaFilter);
    const isMobile = useDeviceStore(selectIsMobile);

    const { minRooms, maxRooms, minPrice, maxPrice, minArea, maxArea } = filters;

    const priceFilterActive = isPriceFilterActive(minPrice, maxPrice);
    const roomFilterActive = minRooms !== FILTERS_MIN_ROOMS || maxRooms !== FILTERS_MAX_ROOMS;
    const areaFilterActive = isAreaFilterActive(minArea, maxArea);

    const getPriceLabel = () => {
        return priceFilterActive ?
            renderPriceSpan(minPrice, maxPrice) :
            getFormattedMessage('jm.components.ResidenceFilters.price');
    };
    const getRoomLabel = () => {
        return roomFilterActive ?
            getFormattedMessage("jm.components.ResidenceFilters.roomSpan", {minRooms: minRooms.toString(), maxRooms: maxRooms.toString(), plus: maxRooms === 5 ? '+' : '' }) :
            getFormattedMessage("jm.components.ResidenceFilters.rooms")
    };
    const getAreaLabel = () => {
        return areaFilterActive ?
            `${minArea} m² - ${maxArea} m²` :
            getFormattedMessage('jm.components.Pricelist.area');
    };

    const onPriceChange = (minPrice: number, maxPrice: number) => {
        setPriceFilter(minPrice, maxPrice);
    };

    const onRoomsChange = (minRooms: number, maxRooms: number) => {
        setRoomsFilter(minRooms, maxRooms);
    };

    const onAreaChange = (minArea: number, maxArea: number) => {
        setAreaFilter(minArea, maxArea);
    };

    if(isMobile) return null;

    return (
        <div className="residence-filters--desktop">
            <FilterPopout
                buttonTitle={getPriceLabel()}
                dataTestId="toggle-price-filter"
                active={priceFilterActive}
                small
            >
                <FormRangeSlider
                    onSliderChange={(value) => onPriceChange(value[0], value[1])}
                    defaultValue={[minPrice, maxPrice]}
                    min={FILTERS_MIN_PRICE}
                    max={FILTERS_MAX_PRICE}
                    label={null}
                    valueSuffix={getFormattedMessage('currency')}
                    maxValueSuffix={getFormattedMessage('currency') + '+'}
                    minRange={100000}
                    step={100000}
                />

            </FilterPopout>

            <FilterPopout
                buttonTitle={getRoomLabel()}
                dataTestId="toggle-room-filter"
                active={roomFilterActive}
                small
            >
                <RoomSelectorV2
                    onChangeRoomsFilter={onRoomsChange}
                    customMinRooms={minRooms}
                    customMaxRooms={maxRooms}
                />
            </FilterPopout>

            <FilterPopout
                buttonTitle={getAreaLabel()}
                dataTestId="toggle-room-filter"
                active={areaFilterActive}
                small
            >
                <FormRangeSlider
                    defaultValue={[minArea, maxArea]}
                    min={FILTERS_MIN_AREA}
                    max={FILTERS_MAX_AREA}
                    minRange={5}
                    valueSuffix="m²"
                    label={null}
                    onSliderChange={(value) => onAreaChange(value[0], value[1])}
                />
            </FilterPopout>
        </div>
    );
};
